import React, { useEffect, useState } from 'react';
import RTSpinner from '../common/RTSpinner';
import RTButton from '../common/RTButton';
import PGNoticeModal from './PGNoticeModal';
import RTPageLayout from '../common/RTPageLayout';
import { apiGet } from '../../api/apiClient';
import { toastError } from '../common/toast';
import { formatDateToDDMMYYYY } from '../../utils/dateHelper';
import { hasPermission } from '../../utils/permissions';
import { formatINR } from './pgFormat';
import './PGPortal.css';

const PGAllocation = () => {
  const [allocation, setAllocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [noticeOpen, setNoticeOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await apiGet('/pg/me/allocation');
      setAllocation(res.data);
    } catch (err) {
      toastError(err.response?.data?.detail || 'Unable to load allocation.');
      setAllocation(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <RTPageLayout
      title="My Allocation Details"
      subtitle="Your bed and stay details"
      mainSection
    >
      <div className="pg-portal">
        {loading ? (
          <RTSpinner message="Loading allocation…" fullScreen={false} />
        ) : !allocation ? (
          <div className="pg-alert">
            No active allocation found. Please contact management.
          </div>
        ) : (
          <>
            {allocation.on_notice ? (
              <div className="pg-alert">
                You are on notice. Stay ends on{' '}
                <strong>{formatDateToDDMMYYYY(allocation.end_date)}</strong>. To
                revoke or change this notice, contact hostel management.
              </div>
            ) : null}

            <div className="pg-card pg-card-accent">
              <div className="pg-card-title">
                <h3>{allocation.pg_name}</h3>
                {allocation.on_notice ? (
                  <span className="pg-badge pg-badge-notice">On notice</span>
                ) : (
                  <span className="pg-badge pg-badge-active">Active</span>
                )}
              </div>

              <div className="pg-row">
                <span className="pg-row-label">Building</span>
                <span className="pg-row-value">{allocation.building_name}</span>
              </div>
              <div className="pg-row">
                <span className="pg-row-label">Floor</span>
                <span className="pg-row-value">
                  {allocation.floor_label || 'N/A'}
                </span>
              </div>
              <div className="pg-row">
                <span className="pg-row-label">Room</span>
                <span className="pg-row-value">
                  {allocation.room_name || allocation.room_type}
                </span>
              </div>
              <div className="pg-row">
                <span className="pg-row-label">Bed</span>
                <span className="pg-row-value">#{allocation.bed_number}</span>
              </div>
              <div className="pg-row">
                <span className="pg-row-label">Start date</span>
                <span className="pg-row-value">
                  {formatDateToDDMMYYYY(allocation.start_date)}
                </span>
              </div>
              <div className="pg-row">
                <span className="pg-row-label">End date</span>
                <span className="pg-row-value">
                  {allocation.end_date
                    ? formatDateToDDMMYYYY(allocation.end_date)
                    : '—'}
                </span>
              </div>
              {allocation.notice_date ? (
                <div className="pg-row">
                  <span className="pg-row-label">Notice date</span>
                  <span className="pg-row-value">
                    {formatDateToDDMMYYYY(allocation.notice_date)}
                  </span>
                </div>
              ) : null}
              <div className="pg-row">
                <span className="pg-row-label">Monthly rent</span>
                <span className="pg-row-value">
                  {formatINR(allocation.payable_amount)}
                </span>
              </div>
              <div className="pg-row">
                <span className="pg-row-label">Advance paid</span>
                <span className="pg-row-value">
                  {formatINR(allocation.advance_paid)}
                </span>
              </div>
            </div>

            {!allocation.on_notice && hasPermission('pg-allocation.notice') ? (
              <div className="pg-card">
                <div className="pg-card-title">
                  <h3>Leaving soon?</h3>
                </div>
                <p className="pg-muted" style={{ marginTop: 0 }}>
                  Submit a notice to vacate. Notice should be submitted minimum
                  of 30 days before vacating.
                </p>
                <div className="pg-actions">
                  <RTButton
                    variant="primary"
                    onClick={() => setNoticeOpen(true)}
                  >
                    Give notice to vacate
                  </RTButton>
                </div>
              </div>
            ) : null}
          </>
        )}
      </div>

      <PGNoticeModal
        isOpen={noticeOpen}
        onClose={() => setNoticeOpen(false)}
        onSuccess={(alloc) => setAllocation(alloc)}
      />
    </RTPageLayout>
  );
};

export default PGAllocation;
